import React, { useCallback, useContext, useEffect, useState } from 'react'
import firebase from 'firebase/app'
import debounce from 'lodash.debounce'
import Button from '@material-ui/core/Button'
import CssBaseline from '@material-ui/core/CssBaseline'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import Container from '@material-ui/core/Container'
import { FirestoreContext } from '../../providers/firestore-provider'
import { UsernameMessage } from '../username-message'
import { getDocumentFromFirestore, IInvitation, IUser } from 'daily-dose-common'
import { firestore } from '../../firebase-init'

// TODO: TextField aren ot longer supported according to Google Material Design so this needs to be refactor
// https://medium.com/google-design/the-evolution-of-material-designs-text-fields-603688b3fe03

//TODO: Implement forget password

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(2, 0, 2),
  },
  submitWithGoogle: {
    margin: theme.spacing(0, 0, 2),
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}))

const UsernameForm = () => {
  const classes = useStyles()

  const [formValue, setFormValue] = useState('')
  const [isValid, setIsValid] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isMounted, setIsMounted] = useState(true)

  const { currentUser } = useContext(FirestoreContext)

  useEffect(() => {
    checkUsername(formValue)
    return () => setIsMounted(false) // use effect cleanup to set flag false, if unmounted
    // eslint-disable-next-line
  }, [formValue])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    // Create refs for both documents
    const userDoc = firestore.doc(`users/${currentUser?.id}`)
    const usernameDoc = firestore.doc(`usernames/${formValue}`)
    const invitationDocRef = firestore.doc(`invitations/${currentUser?.email}`)

    try {
      // Commit both docs together as a batch write.
      setLoading(true)
      const batch = firestore.batch()
      batch.set(
        userDoc,
        {
          username: formValue,
        },
        {
          merge: true,
        }
      )
      batch.set(usernameDoc, { id: currentUser?.id })
      const { exists } = await invitationDocRef.get()
      if (exists) {
        const invitation = (await getDocumentFromFirestore(
          invitationDocRef
        )) as IInvitation

        // invite current user
        const invitationCircleRef = firestore.doc(`circles/${invitation.circle.id}`)
        const member = {
          createdAt: currentUser?.createdAt,
          displayName: currentUser?.displayName,
          email: currentUser?.email,
          id: currentUser?.id,
          photoURL: currentUser?.photoURL,
          username: formValue,
        } as IUser

        // await addMemberToCircle(member, invitationCircleRef)
        batch.update(invitationCircleRef, {
          members: firebase.firestore.FieldValue.arrayUnion(member),
          membersIds: firebase.firestore.FieldValue.arrayUnion(member?.id),
          membersUsernames: firebase.firestore.FieldValue.arrayUnion(
            member?.username
          ),
        })
        // delete invitation document
        // await invitationDocRef.delete()
        batch.delete(invitationDocRef)
      }
      await batch.commit()
      setLoading(false)
    } catch (error) {
      setLoading(false)
      console.warn('Error creating username ', error)
    }
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Force form value typed in form to match correct format
    const val = e.target.value.toLowerCase()
    const re = /^(?=[a-zA-Z0-9._]{3,15}$)(?!.*[_.]{2})[^_.].*[^_.]$/

    // Only set form value if length is < 3 OR it passes regex
    if (val.length < 3) {
      setFormValue(val)
      setLoading(false)
      setIsValid(false)
    }

    if (re.test(val)) {
      setFormValue(val)
      setLoading(true)
      setIsValid(false)
    }
  }

  // Hit the database for username match after each debounced change
  // useCallback is required for debounce to work
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkUsername = useCallback(
    debounce(async (username) => {
      if (username.length >= 3 && isMounted) {
        const ref = firestore.doc(`usernames/${username}`)
        const { exists } = await ref.get()
        setIsValid(!exists)
        setLoading(false)
      }
    }, 500),
    []
  )

  // TODO: Use loading using ui.loading
  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Typography component="header" variant="h3">
          Choose username
        </Typography>
        <form className={classes.form} noValidate onSubmit={handleSubmit}>
          <TextField
            value={formValue}
            onChange={onChange}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            name="username"
            autoComplete="off"
            autoFocus
          />

          <UsernameMessage
            username={formValue}
            isValid={isValid}
            loading={loading}
            successMessage={`${formValue} is available `}
            errorMessage={`${formValue} is taken. Choose another username`}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            disabled={!isValid || loading}
          >
            Choose
          </Button>
        </form>
      </div>
    </Container>
  )
}

export default UsernameForm
